
import React, { useRef } from "react";
import { useFrame } from "@react-three/fiber";
import { Float, MeshDistortMaterial } from "@react-three/drei";
import * as THREE from "three";

const AIEnhancedBall = ({ isTalking }: { isTalking: boolean }) => {
  const ballRef = useRef<THREE.Group>(null);
  const coreRef = useRef<THREE.Mesh>(null);
  const ringRef = useRef<THREE.Mesh>(null);
  const outerRef = useRef<THREE.Mesh>(null);
  
  // Animate the AI ball
  useFrame((state) => {
    const time = state.clock.getElapsedTime();
    
    // Base rotation
    if (ballRef.current) {
      ballRef.current.rotation.y = time * 0.2;
    }
    
    // Core pulsing when talking
    if (coreRef.current) {
      if (isTalking) {
        // More energetic pulsing when talking
        const pulseScale = 0.8 + Math.sin(time * 8) * 0.1 + Math.sin(time * 15) * 0.05;
        coreRef.current.scale.set(pulseScale, pulseScale, pulseScale);
        
        // Brighter when talking
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          2.5 + Math.sin(time * 10) * 0.5;
      } else {
        // Gentle pulsing when idle
        const idlePulse = 0.75 + Math.sin(time * 2) * 0.05;
        coreRef.current.scale.set(idlePulse, idlePulse, idlePulse);
        
        // Dimmer when idle
        (coreRef.current.material as THREE.MeshStandardMaterial).emissiveIntensity = 
          1.5 + Math.sin(time * 3) * 0.2;
      }
    }
    
    // Rotating ring with oscillation
    if (ringRef.current) {
      ringRef.current.rotation.x = time * 0.5;
      ringRef.current.rotation.z = time * 0.3;
      
      // Ring gets more active when talking
      if (isTalking) {
        const ringOscillation = 1 + Math.sin(time * 10) * 0.1;
        ringRef.current.scale.set(ringOscillation, ringOscillation, ringOscillation);
      }
    }
    
    // Outer layer pulsing
    if (outerRef.current) {
      const breathe = 1.0 + Math.sin(time * 0.8) * 0.03;
      outerRef.current.scale.set(breathe, breathe, breathe);
      
      if (isTalking) {
        (outerRef.current.material as THREE.MeshPhysicalMaterial).emissiveIntensity = 
          1.0 + Math.sin(time * 6) * 0.3;
      }
    }
  });

  return (
    <Float rotationIntensity={0.2} floatIntensity={0.5} speed={1.5}>
      <group ref={ballRef}>
        {/* Enhanced outer translucent shell */}
        <mesh ref={outerRef}>
          <sphereGeometry args={[1.3, 64, 64]} />
          <meshPhysicalMaterial 
            color="#88ccff"
            transparent={true}
            opacity={0.3}
            roughness={0.1}
            metalness={0.9}
            clearcoat={1.0}
            clearcoatRoughness={0.1}
            transmission={0.85}
            ior={1.5}
            emissive="#4499ff"
            emissiveIntensity={0.8}
          />
        </mesh>
        
        {/* Secondary layer */}
        <mesh>
          <sphereGeometry args={[1.1, 48, 48]} />
          <meshPhysicalMaterial 
            color="#77bbff"
            transparent={true}
            opacity={0.4}
            roughness={0.2}
            metalness={0.8}
            clearcoat={0.9}
            emissive="#4488ff"
            emissiveIntensity={1.2}
          />
        </mesh>
        
        {/* Outer translucent shell (from original AIBall) */}
        <mesh>
          <sphereGeometry args={[1, 64, 64]} />
          <meshPhysicalMaterial 
            color="#88ccff"
            transparent={true}
            opacity={0.4}
            roughness={0.1}
            metalness={0.9}
            clearcoat={1.0}
            clearcoatRoughness={0.1}
            transmission={0.9}
            ior={1.5}
          />
        </mesh>
        
        {/* Inner energetic core - enhanced brightness */}
        <mesh ref={coreRef}>
          <sphereGeometry args={[0.7, 32, 32]} />
          <meshStandardMaterial
            color="#55aaff"
            emissive="#1177ff"
            emissiveIntensity={2.0}
            roughness={0.2}
            metalness={0.8}
          />
        </mesh>
        
        {/* Energy particles inside (small spheres) - more vibrant */}
        {[...Array(20)].map((_, i) => {
          // Position particles in a spherical pattern inside the ball
          const theta = Math.random() * Math.PI * 2;
          const phi = Math.random() * Math.PI;
          const r = 0.5 + Math.random() * 0.2;
          const x = r * Math.sin(phi) * Math.cos(theta);
          const y = r * Math.sin(phi) * Math.sin(theta);
          const z = r * Math.cos(phi);
          
          return (
            <mesh key={i} position={[x, y, z]}>
              <sphereGeometry args={[0.02 + Math.random() * 0.03, 8, 8]} />
              <meshStandardMaterial 
                color="#ffffff" 
                emissive="#99ddff"
                emissiveIntensity={3.0}
              />
            </mesh>
          );
        })}
        
        {/* Orbiting energy ring - enhanced */}
        <mesh ref={ringRef}>
          <torusGeometry args={[1.3, 0.06, 16, 100]} />
          <meshStandardMaterial 
            color="#55aaff"
            emissive="#2299ff"
            emissiveIntensity={2.0}
            roughness={0.3}
            metalness={0.8}
          />
        </mesh>
        
        {/* Dynamic energy waves when talking */}
        {isTalking && (
          <>
            <mesh>
              <sphereGeometry args={[1.1, 32, 32]} />
              <MeshDistortMaterial
                color="#66aaff"
                transparent={true}
                opacity={0.5}
                speed={5}
                distort={0.4}
              />
            </mesh>
            <mesh>
              <sphereGeometry args={[1.5, 24, 24]} />
              <meshStandardMaterial
                color="#77bbff"
                transparent={true}
                opacity={0.1}
                emissive="#88ccff"
                emissiveIntensity={1.0}
              />
            </mesh>
          </>
        )}
      </group>
    </Float>
  );
};

export default AIEnhancedBall;
